import React, { useState, useEffect } from 'react';
import { isMobile } from '@buzzfeed/bf-utils/lib/device';
import { triggerEnhancedWebviewFeature } from '@buzzfeed/bf-utils/lib/webview';
import { addParams } from '@buzzfeed/bf-utils/lib/query-string';
import { BFIslandButton } from '@buzzfeed/react-components';
import { useTrackingContext, useViewImpression } from '../../../hooks';
import styles from './index.module.scss';
import { CrownIcon, CreateIcon, StopwatchIcon } from '../../../icons/Icons';

const ChallengeCard = ({
  item,
  accentColor = 'yellow',
  packageType = 'challenge',
  navigationSource = 'page',
  isWeb = false,
  isStandaloneCard = false,
  onWebNavigate,
  sharedTrackingInfo,
}) => {
  const { trackInternalLink } = useTrackingContext();
  const isChallenge = packageType === 'challenge';

  const [isMobileDevice, setIsMobileDevice] = useState(
    typeof window !== 'undefined' && isMobile()
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setMounted(true);

    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const title =
    item?.content?.bfp?.data?.intro?.card?.actionTitle ||
    item?.content?.bfp?.data?.intro?.card?.title ||
    '';
  const imageUrl =
    item?.content?.bfp?.data?.intro?.card?.backgroundImage?.url || '';
  const creationCount =
    item?.content?.comments?.by_type?.ai_generation?.count || 0;
  const timestamp = item?.created_at;
  const ctaText = item?.content?.bfp?.data?.intro?.card?.buttonCTA || 'View';
  const itemId = item?.id;
  const itemType = item?.object_type || 'bfp_content';

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  const trackingData = {
    ...sharedTrackingInfo,
    item_type: 'card',
    item_name: itemId,
    target_content_type: 'widget',
    target_content_id: itemId,
  };

  const { setObservable } = useViewImpression({
    trackingData,
  });

  const onNavigate = (e) => {
    if (e?.stopPropagation) {
      e.stopPropagation();
    }
    trackInternalLink(trackingData);

    if (isWeb && onWebNavigate) {
      onWebNavigate(itemType, itemId);
      return;
    }

    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/webview/feed/content-objects/${itemType}/${itemId}`,
        queryParams: {
          source: navigationSource,
          type: 'creation',
        },
      },
      version: 1,
    });
  };

  const hasBottomFullWidthBtn = isStandaloneCard && !isMobileDevice;

  return (
    <div
      className={`${styles.challengeCard} ${isWeb ? styles.web : ''} ${isStandaloneCard ? styles.standalone : ''}`}
      style={{
        '--accent-color': `var(--bfi-${accentColor})`,
        '--accent-text-color': `var(--bfi-${accentColor}-text)`,
      }}
      ref={(el) => setObservable(el)}
      role="button"
      tabIndex={0}
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate();
        }
      }}
    >
      <div className={styles.cardContent}>
        {imageUrl && (
          <img
            src={addParams(imageUrl, { resize: '650:*' })}
            alt={title}
            className={styles.image}
            loading="lazy"
          />
        )}
        <div className={styles.container}>
          <div className={styles.content}>
            <header
              className={`${styles.header} ${styles[`header--${packageType}`] || ''}`}
            >
              {isChallenge && (
                <div className={styles.challengeLabel}>
                  <div className={styles.challengeIconContainer}>
                    <CrownIcon />
                    <span>Challenge</span>
                  </div>
                </div>
              )}
              <div className={styles.likes}>
                <CreateIcon />
                <span>{creationCount}</span>
              </div>
            </header>

            <h3 className={styles.challengeTitle}>{title}</h3>

            <div className={styles.meta}>
              <span className={styles.timestamp}>
                <StopwatchIcon />
                {formatTimestamp(timestamp)}
              </span>
            </div>
          </div>

          {!hasBottomFullWidthBtn && mounted && (
            <BFIslandButton
              text={ctaText}
              onClick={onNavigate}
              variant="light"
              bgColor="var(--accent-color)"
              color="var(--accent-text-color)"
              size={isMobileDevice ? 'small' : 'medium'}
              fullWidth
            />
          )}
        </div>
      </div>

      {hasBottomFullWidthBtn && mounted && (
        <BFIslandButton
          text={ctaText}
          onClick={onNavigate}
          variant="light"
          bgColor="var(--accent-color)"
          color="var(--accent-text-color)"
          size="large"
          fullWidth
        />
      )}
    </div>
  );
};

export default ChallengeCard;
